/**
 *
 * AdminUsers
 *
 */

import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory, useParams } from 'react-router-dom';
import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import _get from 'lodash/get';
import _orderBy from 'lodash/orderBy';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import AppContainer from 'components/AppContainer';
import LoadingOverlay from 'components/LoadingOverlay';
import Header from 'components/Header';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import Avatar from '@material-ui/core/Avatar';
import TablePagination from '@material-ui/core/TablePagination';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import { makeSelectProfile } from 'containers/Auth/selectors';
import ButtonBase from '@material-ui/core/ButtonBase';
import Checkbox from '@material-ui/core/Checkbox';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Popper from '@material-ui/core/Popper';
import Paper from '@material-ui/core/Paper';
import ListItemText from '@material-ui/core/ListItemText';
import SearchBar from 'containers/Admin/ClientsTool/components/SearchBar';
import Table from './components/TableComponents/StyledTable';
import TableHeadCell from './components/TableComponents/StyledTableHeadCell';
import TableRow from './components/TableComponents/StyledTableRow';
import TableCell from './components/TableComponents/StyledTableCell';
import {
  makeSelectData,
  makeSelectValidBrands,
  makeSelectProcessing,
} from './selectors';
import { getAdminUsers, getValidBrands, updateAdminUserRole } from './actions';
import reducer from './reducer';
import saga from './saga';

const useStyles = makeStyles(theme => ({
  action: {
    height: 'fit-content',
    marginRight: 4,
    marginLeft: 4,
    '&:first-child': {
      marginLeft: 0,
    },
    '&:last-child': {
      marginRight: 0,
    },
  },
  tableBody: {
    '& > tr > td:first-child': {
      width: '12%',
    },
    '& > tr > td:last-child': {
      width: '40%',
    },
  },
  link: {
    ...theme.typography.overline,
    cursor: 'pointer',
  },
  caption: {
    ...theme.typography.overline,
  },
  search: {
    margin: '30px 0 30px',
    '& input': {
      width: 348,
    },
  },
  popper: {
    marginTop: 10,
    zIndex: 999,
    '&[x-placement*="bottom"] $arrow': {
      top: 0,
      left: 0,
      marginTop: '-0.9em',
      width: '3em',
      height: '1em',
      '&::before': {
        borderWidth: '0 1em 1em 1em',
        borderColor: `transparent transparent #E3E3E3 transparent`,
      },
    },
  },
  paper: {
    borderRadius: '4px !important',
    boxShadow: 'none !important',
    border: '1px solid #E3E3E3',
  },
  list: {
    padding: 0,
  },
  arrow: {
    position: 'absolute',
    fontSize: 7,
    width: '3em',
    height: '3em',
    '&::before': {
      content: '""',
      margin: 'auto',
      display: 'block',
      width: 0,
      height: 0,
      borderStyle: 'solid',
    },
  },
  button: {
    ...theme.typography.body2,
    color: theme.palette.text.primary,
  },
  text: {
    ...theme.typography.body2,
  },
  textActive: {
    color: theme.palette.primary.main,
  },
  listIcon: {
    minWidth: 37,
  },
  downIcon: {
    marginLeft: 10,
  },
  actionsWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    flex: 1,
    alignItems: 'center',
  },
  bulkBtn: {
    width: 122,
    height: 45,
    borderRadius: 5,
    border: '1px solid #DCDCDC',
    '&.Mui-disabled': {
      background: '#EEEEEE',
    },
  },
  addUserBtn: {
    height: '100%',
    borderRadius: 5,
    marginRight: 4,
  },
  bulkBtnWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
}));

const INITIAL_STATE = {
  order: 'asc',
  orderBy: 'displayName',
  page: 0,
};

const ON_PAGE_CHANGE = 'UserReviews/on-page-change';
const ON_ORDER_CHANGE = 'UserReviews/on-order-change';
const ON_ORDER_BY_CHANGE = 'UserReviews/on-order-by-change';

const sortByReducer = (state, action) => {
  switch (action.type) {
    case ON_PAGE_CHANGE:
      return {
        ...state,
        page: action.payload,
      };
    case ON_ORDER_CHANGE:
      return {
        ...state,
        order: state.order === 'asc' ? 'desc' : 'asc',
      };
    case ON_ORDER_BY_CHANGE:
      return {
        ...state,
        order: 'asc',
        orderBy: action.payload,
      };
    default:
      throw new Error();
  }
};

const AdminMangeUsers = () => {
  useInjectReducer({ key: 'adminManageUsers', reducer });
  useInjectSaga({ key: 'adminManageUsers', saga });

  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { brand } = useParams();
  const [searchText, setSearchText] = useState('');
  const [selected, setSelected] = useState([]);
  const [arrowRef, setArrowRef] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [orderState, orderDispatch] = useReducer(sortByReducer, INITIAL_STATE);
  const processing = useSelector(makeSelectProcessing());
  const validBrands = useSelector(makeSelectValidBrands());
  const data = useSelector(makeSelectData());
  const profile = useSelector(makeSelectProfile());

  const { order, orderBy, page } = orderState;

  useEffect(() => {
    if (profile.isLoaded) {
      if (profile.role !== 'admin' && profile.role !== 'contentAdmin') {
        history.push('/');
      } else {
        dispatch(getAdminUsers({ brand }));
      }
    }
  }, [profile]);

  useEffect(() => {
    if (validBrands === null) {
      dispatch(getValidBrands());
    } else if (!validBrands.includes(brand)) {
      history.replace('/admin/clients-tool');
    }
  }, [validBrands]);

  const filteredData = searchText
    ? data.filter(
        el =>
          (_get(el, 'displayName') || '').toLowerCase().includes(searchText) ||
          (_get(el, 'email') || '').toLowerCase().includes(searchText),
      )
    : data;

  const paginatedData = useMemo(() => {
    const orderByParam =
      orderBy !== 'custom_role' ? orderBy : ['custom_role', 'full_name'];
    const orderParam = orderBy !== 'custom_role' ? order : [order, 'asc'];
    const orderedData = _orderBy(filteredData, orderByParam, orderParam);
    return orderedData.slice(page * 25, (page + 1) * 25);
  }, [filteredData, order, orderBy, page]);

  const handleOrderRequest = property => {
    if (orderBy === property) orderDispatch({ type: ON_ORDER_CHANGE });
    else orderDispatch({ type: ON_ORDER_BY_CHANGE, payload: property });
  };

  const handleChangePage = (_, newPage) => {
    orderDispatch({ type: ON_PAGE_CHANGE, payload: newPage });
  };

  const handleChangeRole = (id, isAdmin) => {
    dispatch(updateAdminUserRole({ userId: id, isAdmin: !isAdmin }));
  };

  const inputHandler = value => {
    const lowerCase = value.toLowerCase();
    setSearchText(lowerCase);
  };

  const handleSelect = id => {
    if (selected.includes(id)) {
      setSelected(selected.filter(el => el !== id));
    } else {
      setSelected([...selected, id]);
    }
  };

  const selectAll = () => {
    if (selected.length === paginatedData.length) {
      setSelected([]);
    } else {
      setSelected(paginatedData.map(el => el.id));
    }
  };
  const handleClick = event => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };
  const handleClose = event => {
    if (anchorEl.contains(event.target)) {
      return;
    }
    setAnchorEl(null);
  };
  const handleListItemClick = isAdmin => {
    setAnchorEl(null);
    dispatch(updateAdminUserRole({ userId: selected, isAdmin }));
  };

  const open = Boolean(anchorEl);
  const id = open ? 'transitions-popper' : undefined;

  return (
    <>
      <Header title="Manage Users" />
      <AppContainer>
        <LoadingOverlay show={processing} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Button
              variant="outlined"
              classes={{ root: classes.action }}
              onClick={() => history.push('/admin/clients-tool')}
            >
              Back
            </Button>
          </Grid>
          <Grid item xs={6}>
            <SearchBar onQueryChange={inputHandler} query={searchText} />
          </Grid>
          <Grid item xs={6} className={classes.bulkBtnWrapper}>
            <Button
              size="large"
              variant="contained"
              color="primary"
              classes={{ root: classes.addUserBtn }}
              onClick={() =>
                history.push(`/admin/clients-tool/add-user/${brand}`)
              }
            >
              Add User
            </Button>
            <ButtonBase
              aria-describedby={id}
              onClick={handleClick}
              className={classes.bulkBtn}
              disableRipple
              disabled={selected.length < 2}
            >
              Bulk Action
            </ButtonBase>
            <Popper
              id={id}
              open={open}
              anchorEl={anchorEl}
              placement="bottom"
              transition
              modifiers={{
                flip: {
                  enabled: true,
                },
                preventOverflow: {
                  enabled: true,
                  boundariesElement: 'scrollParent',
                },
                arrow: {
                  enabled: true,
                  element: arrowRef,
                },
              }}
              className={classes.popper}
            >
              {() => (
                <>
                  <span className={classes.arrow} ref={setArrowRef} />
                  <ClickAwayListener onClickAway={handleClose}>
                    <Paper className={classes.paper}>
                      <List className={classes.list}>
                        <ListItem
                          button
                          onClick={() => handleListItemClick(true)}
                        >
                          <ListItemText
                            primary="Change To Customer Admin"
                            className={classes.text}
                          />
                        </ListItem>
                        <ListItem
                          button
                          onClick={() => handleListItemClick(false)}
                        >
                          <ListItemText
                            primary="Change To User"
                            className={classes.text}
                          />
                        </ListItem>
                      </List>
                    </Paper>
                  </ClickAwayListener>
                </>
              )}
            </Popper>
          </Grid>

          <Grid item xs={12}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableHeadCell>
                    <Checkbox
                      checked={selected.length === paginatedData.length}
                      onChange={selectAll}
                      color="primary"
                    />
                  </TableHeadCell>
                  <TableHeadCell>
                    <TableSortLabel></TableSortLabel>
                  </TableHeadCell>
                  <TableHeadCell>
                    <TableSortLabel
                      active={orderBy === 'displayName'}
                      direction={order}
                      onClick={() => handleOrderRequest('displayName')}
                    >
                      FULLNAME
                    </TableSortLabel>
                  </TableHeadCell>
                  <TableHeadCell>
                    <TableSortLabel
                      active={orderBy === 'email'}
                      direction={order}
                      onClick={() => handleOrderRequest('email')}
                    >
                      EMAIL
                    </TableSortLabel>
                  </TableHeadCell>
                  <TableHeadCell>
                    <TableSortLabel
                      active={orderBy === 'role'}
                      direction={order}
                      onClick={() => handleOrderRequest('custom_role')}
                    >
                      ROLE
                    </TableSortLabel>
                  </TableHeadCell>
                  <TableHeadCell>ACTION</TableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody className={classes.tableBody}>
                {paginatedData.map(row => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Checkbox
                        checked={selected.includes(row.id)}
                        onChange={() => handleSelect(row.id)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      <Avatar
                        src={row.avatarUrl}
                        className={classes.avatar}
                        alt={row.displayName}
                      />
                    </TableCell>
                    <TableCell>{row.displayName}</TableCell>
                    <TableCell>
                      <Typography variant="overline">{row.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="overline">
                        {row.custom_role}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="overline"
                        onClick={() =>
                          handleChangeRole(
                            row.id,
                            !!_get(row, 'isClientAdmin', false),
                          )
                        }
                        style={{ cursor: 'pointer' }}
                        color="primary"
                      >
                        Change To{' '}
                        {_get(row, 'isClientAdmin', false)
                          ? 'User'
                          : 'Customer Admin'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[25]}
              component="div"
              count={filteredData.length}
              rowsPerPage={25}
              page={page}
              onPageChange={handleChangePage}
              classes={{
                caption: classes.caption,
              }}
            />
          </Grid>
        </Grid>
      </AppContainer>
    </>
  );
};

export default AdminMangeUsers;
